// Shared domain types for tips-crm (SPEC-1). Imported by api, web, widget.

export type SellerStatus = 'active' | 'inactive';
export type Channel = 'voice' | 'chat';
export type Direction = 'inbound' | 'outbound';
export type Role = 'platform_admin' | 'agent' | 'reseller_admin' | 'reseller_user';

export type ConversationStatus =
  | 'initiating'
  | 'queued'
  | 'active'
  | 'completed'
  | 'failed'
  | 'abandoned';

export type NoteType = 'manual' | 'ai_summary' | 'disposition' | 'system';
export type AuthorType = 'user' | 'system';
export type SenderType = 'customer' | 'agent' | 'system';
export type MessageSource = 'connect_chat' | 'contact_lens' | 'manual_import';
export type ArtifactType = 'recording' | 'transcript' | 'contact_lens_summary' | 'attachment';

export interface Seller {
  id: string;
  name: string;
  status: SellerStatus;
  created_at: string;
}

export interface Brand {
  id: string;
  seller_id: string;
  name: string;
  logo_url: string | null;
  primary_color: string | null;
  support_display_name: string | null;
  privacy_url: string | null;
  welcome_copy: string | null;
  created_at: string;
}

export interface ResellerSite {
  id: string;
  seller_id: string;
  brand_id: string;
  domain: string;
  allowed_origins: string[];
  widget_public_key: string;
  status: SellerStatus;
  created_at: string;
}

export interface PhoneNumberMapping {
  id: string;
  seller_id: string;
  brand_id: string | null;
  phone_number_e164: string;
  connect_phone_number_id: string | null;
  status: SellerStatus;
  created_at: string;
}

export type PhoneType = 'soft_phone' | 'desk_phone';

export interface User {
  id: string;
  email: string;
  display_name: string;
  role: Role;
  seller_id: string | null;
  status: SellerStatus;
  created_at: string;
  // Contact-center dialing identity (set when provisioned as an agent/SDR).
  extension?: string | null;
  connect_user_id?: string | null;
  phone_type?: PhoneType;
  sso_subject?: string | null;
  // Computed per-request permission flags (not DB columns). Derived server-side from the
  // DB role + ZITADEL project roles; surfaced on /auth/me and login so the SPA can gate UI.
  can_distribute_leads?: boolean;
}

export interface CrmContact {
  id: string;
  seller_id: string;
  external_customer_id: string | null;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone_e164: string | null;
  company: string | null;
  lifecycle_status: string | null;
  // Sales rep this lead is allocated to (NULL = unallocated). Restricted reps only see
  // contacts assigned to them (or ones they've called); admins allocate via /contacts/:id/assign.
  assigned_user_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface Conversation {
  id: string;
  seller_id: string;
  brand_id: string | null;
  site_id: string | null;
  contact_id: string | null;
  channel: Channel;
  direction: Direction;
  connect_contact_id: string | null;
  parent_connect_contact_id: string | null;
  queue_name: string | null;
  agent_user_id: string | null;
  status: ConversationStatus;
  started_at: string | null;
  ended_at: string | null;
  source_domain: string | null;
  source_phone_number_e164: string | null;
  disposition: string | null;
  disposition_source: DispositionSource | null;
  created_at: string;
}

// Canonical disposition taxonomy. UI select options AND the AI inference enum both
// derive from this list; the DB column stays free text for backward compatibility.
export const DISPOSITION_VALUES = [
  'resolved',
  'follow_up_required',
  'callback_scheduled',
  'not_interested',
  'wrong_number',
  'no_answer',
  'escalated',
  'sale',
  'other',
] as const;
export type DispositionValue = (typeof DISPOSITION_VALUES)[number];

// Who set conversations.disposition. NULL (legacy rows) is treated as agent-set so the
// AI never overwrites a value it can't attribute.
export type DispositionSource = 'agent' | 'ai';

export type AnalysisStatus = 'pending' | 'running' | 'completed' | 'failed' | 'skipped';
export type AnalysisInputKind = 'transcript' | 'chat' | 'notes_only';
export type ObjectionHandlingGrade = 'poor' | 'fair' | 'good' | 'excellent';

// Deterministic per-call talk metrics, computed from Transcribe Call Analytics output
// (no LLM involved). All durations in milliseconds.
export interface TalkMetrics {
  total_duration_ms: number | null;
  agent_talk_ms: number | null;
  customer_talk_ms: number | null;
  non_talk_ms: number | null;
  /** agent_talk / (agent_talk + customer_talk), 0..1 */
  talk_ratio: number | null;
  longest_agent_monologue_ms: number | null;
  interruptions_agent: number | null;
  interruptions_customer: number | null;
  agent_talk_speed_wpm: number | null;
  customer_talk_speed_wpm: number | null;
  agent_question_count: number | null;
}

export interface CompetitorMention {
  name: string;
  context: string;
}

export interface ObjectionFinding {
  objection: string;
  rep_response: string;
  handling_grade: ObjectionHandlingGrade;
}

export interface AnalysisActionItem {
  title: string;
  due_hint: string | null;
}

export interface AnalysisCallback {
  requested: boolean;
  datetime_iso: string | null;
}

// LLM-judged coaching scores, 1 (poor) .. 5 (excellent). NULL for notes-only analyses.
export interface Scorecard {
  discovery: number;
  objection_handling: number;
  closing: number;
  next_steps_clarity: number;
  comment?: string;
}

export interface ConversationAiAnalysis {
  id: string;
  conversation_id: string;
  seller_id: string;
  contact_id: string | null;
  agent_user_id: string | null;
  status: AnalysisStatus;
  model_id: string | null;
  input_kind: AnalysisInputKind | null;
  attempt_number: number | null;
  summary: string | null;
  disposition_suggested: string | null;
  disposition_confidence: number | null;
  disposition_rationale: string | null;
  disposition_applied: boolean;
  next_attempt_note: string | null;
  competitors: CompetitorMention[];
  objections: ObjectionFinding[];
  action_items: AnalysisActionItem[];
  callback: AnalysisCallback | null;
  scorecard: Scorecard | null;
  talk_metrics: TalkMetrics | null;
  sentiment: { agent: number | null; customer: number | null } | null;
  error: string | null;
  created_at: string;
  updated_at: string;
}

// Per-contact call prep: the carry-forward note + attempt history for the next call.
export interface ContactCallPrepAttempt {
  conversation_id: string;
  started_at: string | null;
  channel: Channel;
  direction: Direction;
  disposition: string | null;
  disposition_source: DispositionSource | null;
  summary: string | null;
}

export interface ContactCallPrep {
  prep_note: string | null;
  attempt_count: number;
  last_attempt: ContactCallPrepAttempt | null;
  attempts: ContactCallPrepAttempt[];
}

export type TaskStatus = 'open' | 'in_progress' | 'done' | 'cancelled';

export interface Task {
  id: string;
  seller_id: string;
  contact_id: string | null;
  conversation_id: string | null;
  title: string;
  body: string | null;
  status: TaskStatus;
  assignee_user_id: string | null;
  due_at: string | null;
  created_by_user_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface ConversationNote {
  id: string;
  conversation_id: string;
  note_type: NoteType;
  author_user_id: string | null;
  author_type: AuthorType;
  body: string;
  source: string | null;
  is_editable: boolean;
  created_at: string;
}

export interface ConversationMessage {
  id: string;
  conversation_id: string;
  sender_type: SenderType;
  sender_display_name: string | null;
  body: string;
  sent_at: string;
  source: MessageSource;
  // Per-utterance sentiment from Transcribe Call Analytics (POSITIVE|NEGATIVE|NEUTRAL|MIXED).
  sentiment?: string | null;
}

// Seller-scope resolution result used by API authorization middleware.
export type SellerScope =
  | { mode: 'all' }
  | { mode: 'agent_allowed' }
  | { mode: 'single_seller'; sellerId: string };

// Owner-scope resolution result — layered on top of SellerScope. Non-admin reps
// (`agent`, `reseller_user`) are restricted to their own calls + allocated leads.
export type OwnerScope = { restricted: false } | { restricted: true; userId: string };

/**
 * Distribute `total` whole units across buckets in proportion to `weights`, returning
 * an integer count per bucket that sums EXACTLY to `min(total, possible)`. Uses the
 * largest-remainder (Hamilton) method. When all weights are zero (or negative), falls
 * back to an even split. Used to suggest a performance-weighted lead distribution.
 */
export function weightedSplit(weights: number[], total: number): number[] {
  const n = weights.length;
  if (n === 0 || total <= 0) return new Array(n).fill(0);

  const safe = weights.map((w) => (Number.isFinite(w) && w > 0 ? w : 0));
  const sum = safe.reduce((a, b) => a + b, 0);
  // No positive weights → even split.
  const basis = sum > 0 ? safe : new Array(n).fill(1);
  const basisSum = sum > 0 ? sum : n;

  const exact = basis.map((w) => (w / basisSum) * total);
  const floors = exact.map((x) => Math.floor(x));
  let remaining = total - floors.reduce((a, b) => a + b, 0);

  // Hand out the leftover units to the largest fractional remainders first.
  const order = exact
    .map((x, i) => ({ i, frac: x - Math.floor(x) }))
    .sort((a, b) => b.frac - a.frac);
  const result = [...floors];
  for (let k = 0; k < order.length && remaining > 0; k++) {
    result[order[k]!.i]! += 1;
    remaining--;
  }
  return result;
}

// Connect contact attributes set on every voice/chat contact.
export interface ConnectContactAttributes {
  seller_id: string;
  brand_id?: string;
  site_id?: string;
  source_domain?: string;
  source_phone_number?: string;
  crm_contact_id?: string;
  crm_conversation_id?: string;
  channel: Channel;
  direction: Direction;
  outbound_initiated_by_user_id?: string;
}

// Public widget token claims (signed, public-key verifiable in widget API).
export interface WidgetTokenClaims {
  site_id: string;
  seller_id: string;
  allowed_domains: string[];
  iat: number;
  exp: number;
}

export interface ApiError {
  error: string;
  message: string;
  statusCode: number;
}
